import { defineStore } from 'pinia'
import { setSentryUser } from '@wsp-nz-packages/sentry-vue'
import { get, post } from 'src/utils/resource.js'
import { useAppStore } from './appStore.js'
import { appUrl } from 'src/consts.js'
import { setActiveLanguage, getActiveLanguage } from 'src/utils/i18n.js'

// Global store holding auth-related information

export const useAuthStore = defineStore('auth', {
    state: () => ({
        user: null,

        // Loaded from server once user has been authenticated (sourced from laravel policy files e.g UserPolicy)
        permissions: {
            can_impersonate: false,
            can_manage_users: false,
        },
    }),

    getters: {
        isLoggedIn: state => !!state.user,
        isAdmin: state => state.user?.is_super_admin || state.user?.is_admin,
        dataSourceRegion: () => useAppStore().lookups.selected_data_source_region,
        selectedCurrency: state => {
            const appStore = useAppStore()
            return state.user.selected_currency_id
                ? appStore.lookupsById.currency[state.user.selected_currency_id] // If user has selected a specific currency
                : null // Defaulting select null so showing project currency
        },
    },

    actions: {
        // Update logged in user details from the server
        async refreshMyDetails() {
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
            return this.setActiveUser(await get('users/me', { timezone }).catch(() => {}))
        },

        // Refresh users active task counter from the server
        async refreshTaskCount() {
            this.user.num_tasks = await get('users/me/task-count')
        },

        async setLanguage(language) {
            await post('users/me/set-language', { language_id: language.id })
            localStorage.clear() // Must remove filters to avoid translation issues
            location.reload() // Must full reload the app to refresh loaded language file and any already rendered language lines
        },

        // Login as another user
        async impersonate(userId) {
            return this.setActiveUser(await post(`${appUrl}/auth/impersonate/${userId}`))
        },

        // Try logout the current user. Will just re-auth the user since login is automatic
        // Useful for fully reloading the users current session
        async logout(reloadApp = false) {
            await post(`${appUrl}/auth/logout`).catch(() => {}) // Ignore any logout error
            localStorage.clear()
            if (reloadApp) window.location.reload()
        },

        async setActiveUser(userData) {
            // Failed to get active user data, or logging out?
            if (!userData) {
                this.$reset()
                return
            }

            setSentryUser(userData.user)

            // Load all supporting application data into global store once the user has been authenticated
            const appStore = useAppStore()
            appStore.setLookups(userData.lookups)
            appStore.setServices(userData.mapbox, userData.project_locator)

            this.user = userData.user
            this.permissions = userData.permissions

            // Try restore last language selected by the user (if different from the default language)
            let activeLang = getActiveLanguage()
            if (!activeLang || this.user.language != activeLang) {
                await setActiveLanguage(this.user.language)
            }

            return userData.user
        },

        async setRegion(region) {
            localStorage.clear() // Clear out any region-specific items such as adv. filters
            await post('users/me/set-region', { data_source_region_id: region.id })
        },
    },
})
